/**
 * CONFIGURACIÓN DE FEATURE FLAGS
 * 
 * IMPORTANTE:
 * - En desarrollo (DEV_MODE = true): TODOS los módulos habilitados
 * - En producción (DEV_MODE = false): Módulos según plan del cliente
 */

// Modo desarrollo real según entorno Vite.
export const DEV_MODE = import.meta.env.DEV;

// Módulos disponibles en el sistema
export type FeatureName = 
  | 'pos'           // POS (siempre habilitado - plan básico)
  | 'kds'           // KDS (siempre habilitado - plan básico)
  | 'ecommerce'     // Web de Pedidos
  | 'loyalty'       // Fidelización & Wallet
  | 'cod'           // Pedidos contra reembolso (Formulario COD, configuración)
  | 'operaciones'   // Operaciones: Ventas, Logística, Cobranzas (módulo por plan)
  | 'crm'           // CRM y Llamadas (leads, llamadas Zadarma)
  | 'marketing'     // Marketing & Upselling
  | 'logistics'     // Delivery Propio
  | 'commander'     // IA & Chat
  | 'finance'       // Control de Caja
  | 'inventory'     // Existencias Avanzado
  | 'whatsapp'      // WhatsApp (inbox omnicanal, canal por plan)
  | 'chatbot'       // Chatbot Builder (flujos, AI, widget)
  | 'ads'           // Smarty Ads (campañas Meta con IA)
  | 'accounting'    // Contabilidad (comprobantes SUNAT/CPE)
  | 'agents'        // Agentes IA (studio de agentes)
  | 'copilot'       // Copiloto operativo IA (agente que opera el negocio) — solo plan top
  | 'discounts';    // Descuentos y cupones

// Features básicas (siempre habilitadas)
export const BASIC_FEATURES: FeatureName[] = ['pos', 'kds'];

// Planes y sus features
export const PLAN_FEATURES: Record<string, FeatureName[]> = {
  // BASIC = el producto core: una tienda que VENDE de verdad.
  //
  // Antes era solo ['pos','kds'], y eso dejaba la cuenta inutilizable: `create-store` crea
  // todos los tenants nuevos con plan 'basic', así que un cliente que se registraba quedaba
  // SIN Pagos, SIN Checkout, SIN Envíos, SIN Dominios, SIN Marca (todo eso lo gatea
  // `ecommerce`), SIN ver sus pedidos (`operaciones`), sin formulario COD y sin Transporte.
  // Tenía la tienda provisionada y no podía usarla.
  //
  // Cada una es un bloqueante distinto, no un extra:
  //   ecommerce   → Pagos · Checkout · Envíos · Dominios · Marca · Notificaciones
  //   operaciones → ver y gestionar los pedidos que entran
  //   cod         → el formulario COD (el canal principal en Perú)
  //   logistics   → Transporte/agencias: sin esto no puede ENTREGAR
  // Lo que queda como upsell de `pro`: crm, whatsapp, loyalty, chatbot, finance, marketing…
  basic: ['pos', 'kds', 'ecommerce', 'operaciones', 'cod', 'logistics'],
  pro: ['pos', 'kds', 'ecommerce', 'loyalty', 'cod', 'operaciones', 'logistics', 'crm', 'finance', 'whatsapp', 'chatbot'],
  enterprise: [
    'pos',
    'kds',
    'ecommerce',
    'loyalty',
    'cod',
    'operaciones',
    'crm',
    'marketing',
    'logistics',
    'commander',
    'finance',
    'inventory',
    'whatsapp',
    'chatbot',
    'ads',
    'accounting',
    'agents',
    'copilot',
    'discounts',
  ],
};

/** Normaliza slugs de plan desde DB (mayúsculas, espacios, alias legacy). */
export function normalizePlanType(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === '') return 'basic';
  const p = String(raw).toLowerCase().trim();
  if (p === 'premium' || p === 'paid') return 'pro';
  if (p in PLAN_FEATURES) return p;
  return 'basic';
}

// Features habilitadas en modo desarrollo (todos los módulos)
export const DEV_MODE_FEATURES: FeatureName[] = Object.values(PLAN_FEATURES)
  .flat()
  .filter((feature, index, self) => self.indexOf(feature) === index); // Unique

/** Planes de menor a mayor rango. Fuente para derivar "el plan mínimo" de un módulo. */
export const PLAN_ORDER = ['basic', 'pro', 'enterprise'] as const;

/**
 * El plan de menor rango que incluye este módulo (para mostrar "disponible en plan X").
 * Se DERIVA de PLAN_FEATURES — antes se hardcodeaba aparte en MODULES_INFO y divergía.
 */
export function minPlanForModule(key: FeatureName): string {
  for (const plan of PLAN_ORDER) {
    if (PLAN_FEATURES[plan]?.includes(key)) return plan;
  }
  return 'enterprise';
}
